<template>
  <q-dialog
    ref="dialogRef"
    v-model="isVisible"
    persistent
    :transition-show="transitionShow"
    :transition-hide="transitionHide"
    @before-show="onBeforeShow"
    @before-hide="onBeforeHide"
    @show="onShow"
    @shown="onShown"
    @hidden="onHidden"
  >
    <div
      ref="dialogWrapperRef"
      v-bind="$attrs"
      v-drag-dialog
      class="dialog__wrapper tw-relative tw-flex tw-flex-col tw-overflow-hidden"
      :class="[`modal--${props.width}`, props.customClass]"
      :style="modalStyles"
      tabindex="-1"
    >
      <!-- Header (always present, always pinned) -->
      <div
        class="dialog__header-wrapper tw-w-full tw-shrink-0"
        :style="{
          borderTopColor: `rgb(var(--tw-color-${props.primaryColor}))`,
        }"
      >
        <slot name="header" v-bind="{ isLoading, selectedCount }">
          <shared-modal-header
            ref="sharedHeaderRef"
            :config="headerConfig"
            @clear="handleClear"
            @close="closePopup"
            @search="handleSearch"
            @search-submit="handleSearchSubmit"
            @select-all="handleSelectAll"
            @focus-first="focusFirstInBody"
            @focus-last="focusFooterPrimaryAction"
          >
            <!-- Forward the header-second-row slot (replaces entire row 2) -->
            <template v-if="$slots['header-second-row']" #second-row>
              <slot name="header-second-row" />
            </template>
            <!-- Forward the header-controls slot (inserts after FindInput, before Badge) -->
            <template v-if="$slots['header-controls']" #header-controls>
              <slot name="header-controls" />
            </template>
          </shared-modal-header>
        </slot>
      </div>

      <!-- Body (flex container that fills available space) -->
      <div
        ref="bodyRef"
        class="dialog__body tw-relative tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden"
        :class="{ 'tw-p-3': props.bodyPadding }"
      >
        <!-- Loading overlay (when showLoadingOverlay is true) -->
        <div
          v-if="props.showLoadingOverlay"
          class="tw-absolute tw-inset-0 tw-z-50 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-bg-white tw-bg-opacity-80"
        >
          <inner-loader :visible="true" size="50px" transparent />
          <span class="tw-text-sm tw-text-gray-medium-dark">
            {{ props.loadingMessage }}
          </span>
        </div>

        <slot
          :search-query="searchQuery"
          :is-loading="isLoading"
          :selected-count="selectedCount"
        />
      </div>

      <!-- Footer (optional, always pinned) -->
      <slot
        v-if="props.showFooter && !props.hideFooterWhenEmpty"
        name="footer"
        v-bind="{ canSave, isLoading, handleSave, handleCancel }"
      >
        <shared-modal-footer
          ref="sharedFooterRef"
          :config="footerConfig"
          @save="handleSave"
          @cancel="handleCancel"
          @footer-up-navigation="focusLastInBody"
          @footer-down-navigation="focusHeader"
          @toggle-archived="handleToggleArchived"
          @select-none="handleSelectNone"
        >
          <!-- Forward the footer-custom-controls slot for modal-specific controls (row 1 toggles) -->
          <template v-if="$slots['footer-custom-controls']" #custom-controls>
            <slot name="footer-custom-controls" />
          </template>
          <!-- Forward the footer-custom-actions slot for custom action buttons (row 2 buttons) -->
          <template v-if="$slots['footer-custom-actions']" #custom-actions>
            <slot name="footer-custom-actions" />
          </template>
        </shared-modal-footer>
      </slot>
    </div>
  </q-dialog>
</template>

<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<script setup lang="ts">
import {
  ref,
  computed,
  nextTick,
  provide,
  inject,
  toRef,
  watch,
  Ref,
} from 'vue';
import { QDialog } from 'quasar';
import { Colors } from '@/api';
import { useDebounceFn } from '@vueuse/core';
import { useModalKeyboard } from '@/composables/modal/useModalKeyboard';
import { useKeyboardShortcuts } from '@/composables/modal/useKeyboardShortcuts';
import { createLogger } from '@/utils/logger';
import SharedModalHeader from '../shared/SharedModalHeader.vue';
import SharedModalFooter from '../shared/SharedModalFooter.vue';
import InnerLoader from '@/components/core/InnerLoader.vue';
import type {
  ModalWidth,
  ModalTransition,
  SelectAllState,
  ModalContext,
} from '../types';

// Generic props interface
interface Props {
  // Display
  title: string;
  primaryColor?: Colors;
  modelValue?: boolean; // External visibility control (v-model support)

  /**
   * Selection mode for the modal.
   * - 'single': Single item selection mode (Enter auto-selects focused item)
   * - 'multi': Multi-item selection mode (Enter saves current selections)
   */
  mode?: 'single' | 'multi';

  // Size
  width?: ModalWidth;
  height?: number; // Height as vh percentage (e.g., 60 for 60vh)
  customClass?: string;

  // Body
  bodyPadding?: boolean;

  // Header Controls
  clearable?: boolean;
  alwaysClearable?: boolean; // When true, clear is always enabled regardless of selection state
  clearText?: string;
  clearAriaLabel?: string;
  showFind?: boolean;
  findPlaceholder?: string;
  autoFocusFind?: boolean;
  autoFocusPrimaryAction?: boolean;
  autoFocusSlot?: boolean; // Allow slot content to handle auto-focus (e.g., form fields)
  showSelectAll?: boolean;
  showBadgeCount?: boolean;

  // Row Visibility
  showRow2?: boolean;

  // Footer Controls (Row 1 of footer)
  showFooterControls?: boolean;
  showArchivedToggle?: boolean;
  includeArchived?: boolean;
  showNoneOption?: boolean;
  noneOptionText?: string;
  noneSelected?: boolean;

  // Footer
  showFooter?: boolean;
  hideFooterWhenEmpty?: boolean;
  primaryActionText?: string;
  secondaryActionText?: string;
  hideSecondaryAction?: boolean;

  // Behavior
  closeOnSelect?: boolean;
  closeOnSave?: boolean; // Auto-close after save (default true). Set false for async operations.
  closeOnEscape?: boolean;
  preventEnterSubmit?: boolean;
  loading?: boolean;
  disabled?: boolean;

  // Validation
  canSaveValidator?: () => boolean | Promise<boolean>;

  // Loading Overlay
  showLoadingOverlay?: boolean;
  loadingMessage?: string;

  // Transitions
  transitionShow?: ModalTransition;
  transitionHide?: ModalTransition;

  // Performance
  searchDebounce?: number;
  virtualScrollThreshold?: number;

  // Debug
  debugMode?: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void; // v-model support
  (e: 'show'): void;
  (e: 'shown'): void;
  (e: 'hide'): void;
  (e: 'hidden'): void;
  (e: 'save', event?: Event): void;
  (e: 'cancel', event?: Event): void;
  (e: 'clear'): void;
  (e: 'search', query: string, immediate?: boolean): void;
  (e: 'select-all', value: boolean): void;
  (e: 'focus-first'): void;
  (e: 'focus-last'): void;
  (e: 'focus-footer'): void;
  (e: 'focus-header'): void;
  (e: 'focus-body-last'): void;
  (e: 'enter-key', event: KeyboardEvent): void;
  (e: 'escape-key', event: KeyboardEvent): void;
  (e: 'toggle-archived', value: boolean): void;
  (e: 'select-none', value: boolean): void;
  (e: 'toggle-subtasks'): void;
  (e: 'create-key', event: KeyboardEvent): void; // C key when nothing to clear
  (e: 'new-key', event: KeyboardEvent): void; // N key when none option not shown
  // Removed navigate-next-row and navigate-prev-row - BaseModal uses DOM manipulation directly
  (e: 'navigate-left'): void;
  (e: 'navigate-right'): void;
}

const props = withDefaults(defineProps<Props>(), {
  primaryColor: Colors.BLUE,
  width: 'medium',
  height: undefined, // undefined = auto-size, or pass a number for fixed vh height
  bodyPadding: false,
  clearable: false,
  clearText: 'Clear',
  clearAriaLabel: '',
  showFind: false,
  findPlaceholder: 'Find',
  autoFocusFind: true,
  autoFocusPrimaryAction: false,
  autoFocusSlot: false,
  showSelectAll: false,
  showBadgeCount: false,
  showRow2: true,
  showFooterControls: false,
  showArchivedToggle: false,
  includeArchived: false,
  showNoneOption: false,
  noneOptionText: 'None',
  noneSelected: false,
  showFooter: true,
  hideFooterWhenEmpty: false,
  primaryActionText: '', // Will be set in setup
  secondaryActionText: '', // Will be set in setup
  hideSecondaryAction: false,
  closeOnSelect: false,
  closeOnSave: true,
  closeOnEscape: true,
  preventEnterSubmit: false,
  loading: false,
  disabled: false,
  showLoadingOverlay: false,
  loadingMessage: 'Saving...',
  searchDebounce: 300,
  virtualScrollThreshold: 100,
  transitionShow: 'scale',
  transitionHide: 'scale',
  debugMode: false,
});

const emit = defineEmits<Emits>();

// Create logger for this component
const logger = createLogger('BaseModal', {
  enabled: props.debugMode,
});

// Keyboard shortcuts
const { shortcuts, withShortcut } = useKeyboardShortcuts();

// Core refs
const dialogRef = ref<QDialog | null>(null);
const dialogWrapperRef = ref<HTMLElement | null>(null);
const bodyRef = ref<HTMLElement | null>(null);
const sharedHeaderRef = ref<InstanceType<typeof SharedModalHeader>>();
const sharedFooterRef = ref<InstanceType<typeof SharedModalFooter>>();
const primaryButtonRef = ref<HTMLElement | null>(null);
const isVisible = ref(false);
const triggerElement = ref<HTMLElement | null>(null);
const isHiding = ref(false); // Track if hide lifecycle is in progress

// State management
const searchQuery = ref('');
const selectedCount = ref(0);
const selectAllState = ref<SelectAllState>('none');
const isInternalLoading = ref(false);
const hasClearableContent = ref(false);

// Computed
const isLoading = computed(() => props.loading || isInternalLoading.value);
const canSave = computed(() => {
  if (props.disabled || isLoading.value) return false;
  if (props.canSaveValidator) {
    // Handle async validators
    return true; // Will be validated on save
  }
  return true;
});

// Header configuration for SharedModalHeader
const headerConfig = computed(() => ({
  title: props.title,
  primaryColor: props.primaryColor,
  clearable: props.clearable,
  clearText: props.clearText,
  clearAriaLabel: props.clearAriaLabel,
  hasClearableContent:
    props.alwaysClearable || hasClearableContent.value || props.clearable,
  showFind: props.showFind,
  findPlaceholder: props.findPlaceholder,
  autoFocusFind: props.autoFocusFind,
  showSelectAll: props.showSelectAll,
  selectAllState: selectAllState.value,
  showBadgeCount: props.showBadgeCount,
  badgeCount: selectedCount.value,
  searchQuery: searchQuery.value,
  loading: isLoading.value,
  showRow2: props.showRow2,
}));

// Helper functions for keyboard navigation
function detectFocusZone(): 'header' | 'body' | 'footer' {
  const activeElement = document.activeElement;
  if (!activeElement) return 'body';

  // Check header
  if (activeElement.closest('.dialog__header-wrapper, .modal-header')) {
    return 'header';
  }

  // Check footer
  if (
    activeElement.closest('.dialog__footer, .modal-footer, [role="footer"]')
  ) {
    return 'footer';
  }

  return 'body';
}

function focusFirstInBody() {
  // Find the first visible navigable item in the body (including parent row if present)
  const allNavigable = bodyRef.value?.querySelectorAll(
    '[data-navigable="true"]'
  );
  if (allNavigable) {
    // Find first visible item
    for (const item of Array.from(allNavigable)) {
      const element = item as HTMLElement;
      if (
        element.offsetParent !== null ||
        element.offsetWidth > 0 ||
        element.offsetHeight > 0
      ) {
        element.focus();
        element.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
        return;
      }
    }
  }
  // Fallback to emitting event for custom handling
  emit('focus-first');
}

function focusFooter() {
  if (props.showFooter && !props.closeOnSelect) {
    // Only try to focus footer if it has action buttons (not just controls)
    focusPrimaryAction();
  } else {
    // When no footer action buttons, cycle back to the appropriate location
    if (props.showFind) {
      // If find input is visible, go to find input
      focusHeader();
    } else {
      // If neither footer nor find input are visible, go to first item
      focusFirstInBody();
    }
  }
}

function focusLastInBody() {
  // Find the last visible navigable item in the body
  const allNavigable = bodyRef.value?.querySelectorAll(
    '[data-navigable="true"]'
  );
  if (allNavigable && allNavigable.length > 0) {
    // Find last visible item by iterating backwards
    for (let i = allNavigable.length - 1; i >= 0; i--) {
      const element = allNavigable[i] as HTMLElement;
      if (
        element.offsetParent !== null ||
        element.offsetWidth > 0 ||
        element.offsetHeight > 0
      ) {
        element.focus();
        element.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
        return;
      }
    }
  }
  // Fallback to emitting event for custom handling
  emit('focus-body-last');
}

function focusHeader() {
  // Only focus header if find input is shown
  if (!props.showFind) {
    // If no find input, focus first item in body instead
    focusFirstInBody();
    return;
  }

  // Use the shared header's focus method
  if (sharedHeaderRef.value?.focusFind) {
    sharedHeaderRef.value.focusFind();
  } else {
    // Fallback: Try to find search input directly
    // Support both native inputs and QInput components (which render without type attribute)
    const findInput = dialogWrapperRef.value?.querySelector(
      '.dialog__header-wrapper input[type="text"], .dialog__header-wrapper .q-field input'
    ) as HTMLInputElement;
    if (findInput) {
      findInput.focus();
    }
  }
}

/**
 * Navigate between list items in the body
 * This handles all list navigation logic in one place
 */
function navigateListItems(direction: 'up' | 'down') {
  // Find all navigable list items in the body
  // Using custom data-navigable attribute to avoid confusion with user permission roles
  const allItems = bodyRef.value?.querySelectorAll('[data-navigable="true"]');
  if (!allItems || allItems.length === 0) {
    return;
  }

  // Filter out hidden items (elements that are not visible due to v-show or display:none)
  const listItems = Array.from(allItems).filter((item) => {
    const element = item as HTMLElement;
    return (
      element.offsetParent !== null ||
      element.offsetWidth > 0 ||
      element.offsetHeight > 0
    );
  });

  if (listItems.length === 0) {
    return;
  }

  // Find currently focused item
  const currentFocus = document.activeElement as HTMLElement;
  let currentIndex = -1;

  for (let i = 0; i < listItems.length; i++) {
    if (listItems[i] === currentFocus || listItems[i].contains(currentFocus)) {
      currentIndex = i;
      break;
    }
  }

  // Calculate new index
  let newIndex: number;
  if (direction === 'down') {
    if (currentIndex === -1) {
      // Not in list yet, focus first item
      newIndex = 0;
    } else if (currentIndex === listItems.length - 1) {
      // At last item
      const hasFooterActions = props.showFooter && !props.closeOnSelect;
      logger.debug(
        `At last item. showFooter: ${props.showFooter}, closeOnSelect: ${props.closeOnSelect}, hasFooterActions: ${hasFooterActions}, showFind: ${props.showFind}`
      );
      if (hasFooterActions) {
        // Footer with action buttons exists, go to footer
        logger.debug('Going to footer action buttons');
        focusFooter();
        return;
      } else if (props.showFind) {
        // No footer action buttons but find input exists, go to find input
        logger.debug('Going to header (find input)');
        focusHeader();
        return;
      } else {
        // Neither footer action buttons nor find input, cycle to first item
        logger.debug('Cycling to first item');
        newIndex = 0;
      }
    } else {
      // Go to next item
      newIndex = currentIndex + 1;
    }
  } else {
    // direction === 'up'
    if (currentIndex === -1) {
      // Not in list yet, focus last item
      newIndex = listItems.length - 1;
    } else if (currentIndex === 0) {
      // At first item
      if (props.showFind) {
        // Go to header (find input)
        focusHeader();
        return;
      } else if (props.showFooter && !props.closeOnSelect) {
        // No find input but footer with action buttons exists, go to footer
        focusFooter();
        return;
      } else {
        // Neither find input nor footer action buttons, cycle to last item
        newIndex = listItems.length - 1;
      }
    } else {
      // Go to previous item
      newIndex = currentIndex - 1;
    }
  }

  // Focus the new item
  if (listItems[newIndex]) {
    const itemToFocus = listItems[newIndex] as HTMLElement;
    itemToFocus.focus();
    itemToFocus.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    });
  }
}

// Inject optional keyboard helpers from parent components
const keyboardHelpers = inject<{
  getSearchResultsCount?: () => number;
} | null>('keyboardHelpers', null);

// Initialize keyboard handler after all helper functions are defined
useModalKeyboard({
  modalRef: dialogWrapperRef as Ref<HTMLElement | undefined>,
  isVisible, // Pass visibility state to prevent capturing keys when hidden
  mode: props.mode,
  getSearchResultsCount: keyboardHelpers?.getSearchResultsCount || (() => 0),
  focusFirstListItem: () => focusFirstInBody(), // Use the existing function
  debug: props.debugMode,
  handlers: {
    onEnter: async () => {
      logger.debug('Enter key pressed - emitting enter-key for save');
      // The composable handles clicking on list items automatically
      // This handler is only called when NOT on a list item
      const event = new KeyboardEvent('keydown', { key: 'Enter' });
      emit('enter-key', event);
    },

    onCtrlEnter: async () => {
      // Small delay to allow any input field emits to be processed by parent
      // This ensures form values are updated before save validation runs
      await nextTick();
      // Additional small delay to ensure Vue reactivity has completed
      await new Promise((resolve) => setTimeout(resolve, 10));
      // Create a synthetic event that can be prevented by the save handler
      // This allows async save operations to prevent auto-close
      const syntheticEvent = new Event('save', { cancelable: true });
      handleSave(syntheticEvent);
    },

    onEscape: () => {
      logger.debug('Escape key pressed');
      if (props.closeOnEscape) {
        handleCancel();
      }
    },

    onArrowUp: () => {
      const zone = detectFocusZone();
      if (zone === 'header') {
        // From header/find input
        if (props.showFooter && !props.closeOnSelect) {
          // If footer with action buttons exists, go to footer
          focusFooter();
        } else {
          // No footer action buttons, go to last item in body
          focusLastInBody();
        }
      } else if (zone === 'body') {
        // Handle list navigation directly here
        navigateListItems('up');
      } else if (zone === 'footer') {
        // Always go to last item in body from footer
        focusLastInBody();
      }
    },

    onArrowDown: () => {
      const zone = detectFocusZone();
      if (zone === 'header') {
        focusFirstInBody();
      } else if (zone === 'body') {
        // Handle list navigation directly here
        navigateListItems('down');
      } else if (zone === 'footer') {
        // From footer, cycle back to the top
        if (props.showFind) {
          // If find input exists, go to find input
          focusHeader();
        } else {
          // No find input, go directly to first item in body
          focusFirstInBody();
        }
      }
    },

    onArrowLeft: () => {
      // Emit navigation event for hierarchical browsers (like BoardBrowser)
      // This should work from anywhere in the modal to navigate to parent folder
      emit('navigate-left');
    },

    onArrowRight: () => {
      // Emit navigation event for hierarchical browsers (like BoardBrowser)
      const zone = detectFocusZone();
      if (zone === 'body') {
        // Let the browser component handle hierarchical navigation
        emit('navigate-right');
      }
    },

    onSlash: () => {
      // Only focus header if find input is shown
      if (props.showFind) {
        focusHeader();
      }
    },
    onQuestionMark: () => {
      // Only focus header if find input is shown
      if (props.showFind) {
        focusHeader();
      }
    },
    onA: () => emit('toggle-archived', !props.includeArchived),
    onN: () => {
      // Select none if none option is shown, otherwise emit new-key for "new" actions
      if (props.showNoneOption) {
        emit('select-none', !props.noneSelected);
      } else {
        const event = new KeyboardEvent('keydown', { key: 'n' });
        emit('new-key', event);
      }
    },
    onS: () => emit('toggle-subtasks'),
    onC: () => {
      // Clear if clearable - must match headerConfig.hasClearableContent logic
      if (
        props.alwaysClearable ||
        hasClearableContent.value ||
        props.clearable
      ) {
        handleClear();
      } else {
        // Emit create-key for consumers that want to handle C for "create" actions
        const event = new KeyboardEvent('keydown', { key: 'c' });
        emit('create-key', event);
      }
    },
  },
});

// Footer configuration for SharedModalFooter
const footerConfig = computed(() => ({
  primaryColor: props.primaryColor,
  primaryActionText:
    props.primaryActionText || withShortcut('Save', shortcuts.SAVE),
  secondaryActionText:
    props.secondaryActionText || withShortcut('Cancel', shortcuts.CANCEL),
  hideSecondaryAction: props.hideSecondaryAction,
  canSave: canSave.value,
  loading: isLoading.value,
  // Footer Controls
  showFooterControls: props.showFooterControls,
  showArchivedToggle: props.showArchivedToggle,
  includeArchived: props.includeArchived,
  showNoneOption: props.showNoneOption,
  noneOptionText: props.noneOptionText,
  noneSelected: props.noneSelected,
  // Footer Actions - hide when closeOnSelect is true
  showFooterActions: !props.closeOnSelect,
}));

// Theming - using CSS variables directly

// Modal styles
const modalStyles = computed(() => {
  const widthMap: Record<string, string> = {
    small: '350px',
    medium: '420px',
    large: '600px',
    xl: '700px',
    free: 'auto',
  };

  // Support both predefined sizes and custom CSS width strings
  const resolvedWidth = widthMap[props.width] || props.width;

  const baseStyles: Record<string, string> = {
    width: resolvedWidth,
    maxWidth: '95vw',
    '--q-primary': `rgb(var(--tw-color-${props.primaryColor}))`,
  };

  // Only set fixed height if height prop is provided
  // undefined = auto-size based on content
  if (props.height !== undefined) {
    baseStyles.height = `${props.height}vh`;
    baseStyles.maxHeight = `${props.height}vh`;
  } else {
    baseStyles.maxHeight = '85vh'; // Still limit max height to prevent overflow
  }

  return baseStyles;
});

// Search handling with debounce
const handleSearchDebounced = useDebounceFn((query: string) => {
  emit('search', query);
}, props.searchDebounce);

function handleSearch(query: string) {
  searchQuery.value = query;
  handleSearchDebounced(query);
}

function handleSearchSubmit(query: string) {
  // When Enter is pressed in search input, trigger immediate search (bypass debounce)
  searchQuery.value = query;
  emit('search', query, true); // immediate = true
}

// Footer navigation handlers
// Note: Footer navigation is now handled by SharedModalFooter component
// which emits footer-up-navigation and footer-down-navigation events

async function handleSave(event?: Event) {
  if (props.debugMode) {
    logger.debug('handleSave called', {
      canSave: canSave.value,
      disabled: props.disabled,
      isInternalLoading: isInternalLoading.value,
    });
  }
  if (!canSave.value) {
    if (props.debugMode) {
      logger.debug('handleSave aborted - canSave is false');
    }
    return;
  }

  try {
    isInternalLoading.value = true;

    // Run validator if provided
    if (props.canSaveValidator) {
      if (props.debugMode) {
        logger.debug('Running canSaveValidator');
      }
      const isValid = await props.canSaveValidator();
      if (props.debugMode) {
        logger.debug('canSaveValidator returned', { isValid });
      }
      if (!isValid) {
        isInternalLoading.value = false;
        return;
      }
    }

    if (props.debugMode) {
      logger.debug('Emitting save event');
    }
    emit('save', event);

    // Auto-close if enabled and not prevented
    if (props.closeOnSave && !event?.defaultPrevented) {
      closePopup();
    }
  } finally {
    isInternalLoading.value = false;
  }
}

function handleCancel(event?: Event) {
  emit('cancel', event);
  if (!event?.defaultPrevented) {
    closePopup();
  }
}

function handleClear() {
  // Clear search query when clear button is clicked
  searchQuery.value = '';
  emit('clear');
}

function handleSelectAll(value: boolean) {
  emit('select-all', value);
}

function handleToggleArchived(value: boolean) {
  emit('toggle-archived', value);
}

function handleSelectNone(value: boolean) {
  emit('select-none', value);
}

// Lifecycle hooks
function onBeforeShow() {
  logger.trace('onBeforeShow called');
  // Reset hide flag in case previous hide cycle didn't complete (defensive reset)
  isHiding.value = false;
  // Sync internal isVisible with actual dialog state for keyboard handler
  isVisible.value = true;
  // Store current trigger if not already set
  if (!triggerElement.value) {
    triggerElement.value = document.activeElement as HTMLElement;
  }
  // Keyboard activation handled automatically by useSimplifiedKeyboard
  emit('show');
}

function onBeforeHide() {
  // Prevent duplicate calls from both watcher and QDialog event
  if (isHiding.value) {
    logger.trace('onBeforeHide already in progress, skipping');
    return;
  }

  isHiding.value = true;
  logger.trace('onBeforeHide called');
  emit('hide');

  // Return focus to trigger
  if (triggerElement.value && document.contains(triggerElement.value)) {
    // Use requestAnimationFrame to ensure focus happens after modal is hidden
    requestAnimationFrame(() => {
      triggerElement.value?.focus();
    });
  }
}

async function onShow() {
  // @show fires when transition starts, wait one frame for DOM to be ready
  await new Promise((resolve) => requestAnimationFrame(resolve));
  await setInitialFocus();
}

async function onShown() {
  logger.trace('onShown called');
  emit('shown');
  // Focus is already handled in onShow since onShown doesn't fire reliably
}

function onHidden() {
  logger.trace('onHidden called');
  // Sync internal isVisible with actual dialog state for keyboard handler
  isVisible.value = false;
  emit('hidden');
  // Reset state
  searchQuery.value = '';
  triggerElement.value = null;
  isHiding.value = false; // Reset hide flag for next open/close cycle
}

// Watch isVisible to manually trigger hide lifecycle
// QDialog's @before-hide event is not reliably firing, so we handle it ourselves
watch(
  isVisible,
  (newValue, oldValue) => {
    if (oldValue === true && newValue === false && !isHiding.value) {
      // Manually call onBeforeHide since QDialog's @before-hide is not firing
      onBeforeHide();
    }
  },
  { flush: 'sync' } // Force synchronous execution
);

// Focus management
async function setInitialFocus() {
  await nextTick();

  // If autoFocusPrimaryAction is true, focus the primary action button
  // This is useful for simple read-only modals where the user's main action is to close/confirm
  if (props.autoFocusPrimaryAction && props.showFooter) {
    await nextTick();
    await focusFooterPrimaryAction();
    return;
  }

  // If autoFocusSlot is true, let the slot content handle focus FIRST
  // This is essential for form fields and custom input components
  if (props.autoFocusSlot) {
    const slotHandledFocus = await waitForSlotFocus(true); // Pass true for longer wait
    if (slotHandledFocus) return;

    // Even if slot didn't handle focus yet, don't focus wrapper
    // The slot's auto-focus might be delayed (e.g., waiting for component initialization)
    // Focusing wrapper here would steal focus when slot eventually focuses
    return;
  }

  // Ensure the modal wrapper has focus context for keyboard events
  // This ensures keyboard events work regardless of where focus ends up
  // Skip this if slot already handled focus
  if (dialogWrapperRef.value) {
    dialogWrapperRef.value.focus();
  }

  // If we have a find/search input AND autoFocusFind is true, focus it immediately
  // This takes priority over slot focus handling to ensure consistent behavior
  if (
    props.showFind &&
    props.autoFocusFind &&
    sharedHeaderRef.value?.focusFind
  ) {
    // Use deterministic approach instead of setTimeout
    await focusFindWhenReady();
    return;
  }

  // Allow slot to handle focus if no find input with autoFocus and autoFocusSlot wasn't set
  if (!props.autoFocusSlot) {
    const slotHandledFocus = await waitForSlotFocus();
    if (slotHandledFocus) return;
  }

  // If we have a find input but autoFocusFind is false, don't force focus
  if (props.showFind && !props.autoFocusFind) {
    // Focus first item in body instead
    focusFirstInBody();
    return;
  }

  // If no find input shown, focus the first item in the body
  if (!props.showFind) {
    focusFirstInBody();
    return;
  }

  // Fallback: Find input → First form input → Primary button
  const focusTargets = [
    () =>
      dialogWrapperRef.value?.querySelector(
        '.modal-header input[type="text"]'
      ) as HTMLElement,
    () =>
      dialogWrapperRef.value?.querySelector(
        'input:not([type="hidden"]), textarea, select'
      ) as HTMLElement,
    () =>
      primaryButtonRef.value?.querySelector?.('button') ||
      primaryButtonRef.value,
  ];

  for (const getTarget of focusTargets) {
    const target = getTarget();
    if (target && typeof target.focus === 'function') {
      target.focus();
      break;
    }
  }
}

// Deterministic focus management helpers
async function focusFindWhenReady(): Promise<void> {
  await nextTick();

  if (!sharedHeaderRef.value?.focusFind) return;

  // Method 1: Try immediate focus first
  const findInput =
    sharedHeaderRef.value.$el?.querySelector('input[type="text"]');
  if (findInput) {
    findInput.focus();
    return;
  }

  // Method 2: Use MutationObserver for dynamic content
  return new Promise((resolve) => {
    const observer = new MutationObserver((mutations, obs) => {
      const input =
        sharedHeaderRef.value?.$el?.querySelector('input[type="text"]');
      if (input) {
        obs.disconnect();
        input.focus();
        resolve();
      }
    });

    const target = sharedHeaderRef.value?.$el || sharedHeaderRef.value;
    if (target) {
      observer.observe(target, {
        childList: true,
        subtree: true,
      });

      // Safety timeout - but this should rarely be hit
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          observer.disconnect();
          // Try one last time
          sharedHeaderRef.value?.focusFind();
          resolve();
        });
      });
    } else {
      resolve();
    }
  });
}

// Wait for slot to handle focus using event detection
async function waitForSlotFocus(longerWait = false): Promise<boolean> {
  return new Promise((resolve) => {
    const startFocus = document.activeElement;
    let focusHandled = false;

    // Listen for immediate focus changes
    const handleFocus = (e: FocusEvent) => {
      if (
        dialogWrapperRef.value?.contains(e.target as Node) &&
        e.target !== dialogWrapperRef.value &&
        e.target !== startFocus
      ) {
        focusHandled = true;
        cleanup();
        resolve(true);
      }
    };

    const cleanup = () => {
      document.removeEventListener('focusin', handleFocus, true);
    };

    // Use capture phase to detect focus before it bubbles
    document.addEventListener('focusin', handleFocus, true);

    // For autoFocusSlot, wait longer to allow component initialization
    // Standard: 2 frames, Longer: 4 frames
    const maxFrames = longerWait ? 4 : 2;
    let frameCount = 0;

    const checkFrame = () => {
      frameCount++;
      if (focusHandled) return;

      if (frameCount < maxFrames) {
        requestAnimationFrame(checkFrame);
      } else {
        cleanup();
        // Check if focus changed without triggering our event
        const endFocus = document.activeElement;
        const slotTookFocus =
          startFocus !== endFocus &&
          dialogWrapperRef.value?.contains(endFocus as Node);
        resolve(slotTookFocus || false);
      }
    };

    requestAnimationFrame(checkFrame);
  });
}

// Public API
function showPopup(trigger?: HTMLElement) {
  // Reset search query when opening modal
  searchQuery.value = '';

  if (trigger) {
    triggerElement.value = trigger;
  }
  isVisible.value = true;
}

function closePopup() {
  logger.debug('closePopup called');
  // Use v-model to close dialog
  // This triggers our watcher which calls onBeforeHide
  isVisible.value = false;
}

// Watch for external modelValue changes (v-model support)
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== undefined && newValue !== isVisible.value) {
      isVisible.value = newValue;
    }
  },
  { immediate: true } // Run on mount to sync initial modelValue
);

// Emit modelValue updates when isVisible changes
watch(isVisible, (newValue) => {
  emit('update:modelValue', newValue);
});

function updateSelectedCount(count: number) {
  selectedCount.value = count;
}

function updateSelectAllState(state: SelectAllState) {
  selectAllState.value = state;
}

function updateHasClearableContent(value: boolean) {
  hasClearableContent.value = value;
}

// Method to focus the primary action button
async function focusPrimaryAction() {
  // Focus the primary action button in the footer if it exists
  // If no primary action (closeOnSelect=true), try to focus first control in footer

  // Wait for the footer to be fully rendered
  await nextTick();

  // Use requestAnimationFrame to ensure the DOM is painted
  await new Promise((resolve) =>
    requestAnimationFrame(() => resolve(undefined))
  );

  // First try the primary action button
  if (sharedFooterRef.value?.focusPrimaryAction) {
    const focused = await sharedFooterRef.value.focusPrimaryAction();
    if (focused) return;
  }

  // If no primary action button, try footer controls (Archived toggle, None option, etc.)
  const footerControls = dialogWrapperRef.value?.querySelector(
    '.footer-row-1 [tabindex="0"], .footer-row-1 button, .footer-row-1 input'
  ) as HTMLElement;
  if (footerControls) {
    footerControls.focus();
    return;
  }

  // Last fallback
  if (primaryButtonRef.value) {
    const button =
      primaryButtonRef.value.querySelector?.('button') ||
      primaryButtonRef.value;
    button?.focus();
  }
}

// Focus the footer primary action from header (up arrow navigation)
async function focusFooterPrimaryAction() {
  // Only focus footer if it exists and is visible
  if (props.showFooter) {
    await focusPrimaryAction();
  } else {
    // If no footer, focus the last item in the body
    focusLastInBody();
  }
}

// Provide context for child components
const modalContext: ModalContext = {
  isLoading,
  searchQuery: toRef(searchQuery),
  selectedCount: toRef(selectedCount),
  showPopup,
  closePopup,
  focusPrimaryAction,
  focusSearch: focusHeader, // Use the existing focusHeader method
};

provide('modalContext', modalContext);

// Expose public API
defineExpose({
  showPopup,
  closePopup,
  updateSelectedCount,
  updateSelectAllState,
  updateHasClearableContent,
  focusPrimaryAction,
  focusHeader,
  get isVisible() {
    return isVisible.value;
  },
});
</script>

<style lang="scss" scoped>
// Base wrapper styles
.dialog__wrapper {
  @apply tw-bg-white tw-rounded-lg;

  // Remove focus outline since tabindex="-1" is for keyboard event handling only
  &:focus {
    outline: none;
  }
}

// Width classes
.modal--small {
  width: 350px;
}
.modal--medium {
  width: 420px;
}
.modal--large {
  width: 600px;
}
.modal--xl {
  width: 700px;
}
.modal--free {
  width: auto;
}

// Header wrapper
.dialog__header-wrapper {
  border-top: 3px solid; // Color applied via style binding
}

// Body - critical for Firefox scrolling
.dialog__body {
  min-height: 0;
  @apply tw-bg-white;
}

// Footer
.dialog__footer {
  @apply tw-p-3 tw-border-t tw-border-gray-medium;

  // Focus ring for buttons
  :deep(.q-btn):focus-within {
    outline: 1px solid var(--q-primary);
    outline-offset: 1px;
  }
}

// Performance optimizations
.dialog__body {
  // Enable GPU acceleration for smooth scrolling
  will-change: scroll-position;

  // Optimize scroll performance
  scroll-behavior: smooth;
  overscroll-behavior: contain;
}

// Performance optimizations removed to prevent drag delay
// transform: translateZ(0) and backface-visibility interfere with v-drag-dialog
</style>
